import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

interface Props {
  /** One icon from `lucide-react`, 20px standalone with the lighter stroke (§4 Icons). */
  icon: LucideIcon;
  /**
   * One sentence. "No candidates yet" and "No candidates match these filters" are **different
   * states** (§8) — the caller decides which sentence, and passes it translated.
   */
  message: string;
  /** One action, and only one. */
  action?: ReactNode;
  /**
   * What the sentence cannot say on its own, between it and the action — and nothing that competes
   * with either. It exists for one state in the design: §6.2's first-run path, whose sentence is
   * *"Nothing to show yet."* and whose three numbered steps are the content of the state rather
   * than a second sentence or a second action.
   */
  children?: ReactNode;
  className?: string;
}

/**
 * The empty state of `DESIGN.md` §8: an icon, one sentence, one action. A screen is not finished
 * until this exists, so it is a component rather than a pattern each screen re-invents.
 */
export function EmptyState({ icon: Icon, message, action, children, className }: Props) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded border border-rule bg-paper px-8 py-12 text-center',
        className,
      )}
    >
      <Icon size={20} strokeWidth={1.5} className="text-ink-500" aria-hidden />
      <p className="text-sm text-ink-700">{message}</p>
      {children}
      {action}
    </div>
  );
}
